import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RESTAURANTS = [
  "McDonald's", 'Taco Bell', 'Chipotle', 'Sweetgreen',
  'Cava', 'Just Salad', 'Panera Bread', 'Chick-fil-A',
  'Subway', 'Starbucks', "Wendy's", 'Burger King'
];

const EMOJI_MAP = {
  "McDonald's": '🍔', 'Taco Bell': '🌮', 'Chipotle': '🌯',
  'Sweetgreen': '🥗', 'Cava': '🧆', 'Just Salad': '🥬',
  'Panera Bread': '🥖', 'Chick-fil-A': '🍗', 'Subway': '🥪',
  'Starbucks': '☕', "Wendy's": '🍟', 'Burger King': '👑',
};

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 300,
    background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  },
  modal: {
    background: '#fff', borderRadius: '24px',
    width: '100%', maxWidth: '520px', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  header: {
    padding: '1.75rem 1.75rem 1rem',
    position: 'sticky', top: 0, background: '#fff',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontWeight: 800, fontSize: '1.2rem', color: '#111' },
  closeBtn: {
    background: '#f0f0f0', border: 'none', borderRadius: '50%',
    width: '32px', height: '32px', cursor: 'pointer',
    fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666',
  },
  sub: { color: '#666680', fontSize: '0.85rem', marginTop: '0.35rem' },
  body: { padding: '1.5rem 1.75rem' },
  locBtn: {
    width: '100%', padding: '0.9rem', borderRadius: '12px',
    background: 'linear-gradient(135deg, #3a7d00, #1f4500)',
    color: '#fff', fontWeight: 700, fontSize: '1rem',
    border: 'none', cursor: 'pointer', marginBottom: '1.25rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
  },
  status: {
    textAlign: 'center', color: '#666680', fontSize: '0.9rem',
    padding: '1.5rem', background: '#f8f8f8', borderRadius: '12px', marginBottom: '1rem',
  },
  resultCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)',
    marginBottom: '0.75rem', background: '#fafafa', transition: 'all 0.2s',
  },
  resultLeft: { display: 'flex', alignItems: 'center', gap: '0.85rem' },
  emoji: { fontSize: '1.75rem' },
  resultName: { fontWeight: 700, fontSize: '0.95rem', color: '#111' },
  resultDist: { fontSize: '0.8rem', color: '#3a7d00', fontWeight: 600, marginTop: '2px' },
  viewBtn: {
    padding: '0.5rem 1rem', borderRadius: '8px',
    background: 'linear-gradient(135deg, #3a7d00, #1f4500)',
    color: '#fff', fontWeight: 600, fontSize: '0.8rem',
    border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  errorBox: {
    background: '#f0faf0', border: '1px solid rgba(58,125,0,0.2)',
    borderRadius: '12px', padding: '1rem', color: '#3a7d00',
    fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center',
  },
  divider: { textAlign: 'center', color: '#aaa', fontSize: '0.8rem', margin: '1rem 0' },
  manualList: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' },
  manualBtn: (active) => ({
    padding: '0.6rem 0.5rem', borderRadius: '8px', textAlign: 'center',
    border: active ? '2px solid #3a7d00' : '1px solid rgba(0,0,0,0.08)',
    background: active ? 'rgba(58,125,0,0.06)' : '#f8f8f8',
    cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500,
    color: active ? '#3a7d00' : '#444',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  }),
  goBtn: {
    width: '100%', padding: '0.85rem', borderRadius: '12px', marginTop: '1rem',
    background: 'linear-gradient(135deg, #3a7d00, #1f4500)',
    color: '#fff', fontWeight: 700, fontSize: '0.95rem',
    border: 'none', cursor: 'pointer',
  },
};

export default function NearbyModal({ onClose }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);

  const handleLocate = () => {
    setStatus('locating');
    setError('');
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Please select restaurants manually below.');
      setStatus('manual');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setStatus('loading');
        const mockResults = RESTAURANTS.slice(0, 6).map((name, i) => ({
          name,
          distance: (0.3 + i * 0.4).toFixed(1),
        }));
        setResults(mockResults);
        setStatus('done');
      },
      () => {
        setError('Location access was denied. Select restaurants manually below.');
        setStatus('manual');
      }
    );
  };

  const toggleSelect = (name) => {
    setSelected(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };

  const goToFirst = () => {
    if (selected.length > 0) {
      navigate(`/restaurant/${encodeURIComponent(selected[0])}`);
      onClose();
    }
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.titleRow}>
            <div style={s.title}>📍 Find Restaurants Near Me</div>
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={s.sub}>Find nearby fast food and jump straight to their macro guide.</div>
        </div>

        <div style={s.body}>
          {status === 'idle' && (
            <button style={s.locBtn} onClick={handleLocate}>📍 Use My Current Location</button>
          )}
          {status === 'locating' && <div style={s.status}>📍 Getting your location...</div>}
          {status === 'loading' && <div style={s.status}>🔍 Searching for nearby restaurants...</div>}
          {error && <div style={s.errorBox}>{error}</div>}

          {status === 'done' && results.length > 0 && (
            <>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', marginBottom: '1rem' }}>Nearby Restaurants</div>
              {results.map((r, i) => (
                <div key={i} style={s.resultCard}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#3a7d00'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'}
                >
                  <div style={s.resultLeft}>
                    <div style={s.emoji}>{EMOJI_MAP[r.name]}</div>
                    <div>
                      <div style={s.resultName}>{r.name}</div>
                      <div style={s.resultDist}>~{r.distance} mi away</div>
                    </div>
                  </div>
                  <button style={s.viewBtn} onClick={() => { navigate(`/restaurant/${encodeURIComponent(r.name)}`); onClose(); }}>
                    View Macros →
                  </button>
                </div>
              ))}
              <div style={s.divider}>— or browse all —</div>
            </>
          )}

          {(status === 'manual' || status === 'done' || status === 'idle') && (
            <>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111', marginBottom: '0.75rem' }}>
                {status === 'idle' ? 'Or select manually' : status === 'manual' ? 'Select a Restaurant' : 'All Restaurants'}
              </div>
              <div style={s.manualList}>
                {RESTAURANTS.map(name => (
                  <div key={name} style={s.manualBtn(selected.includes(name))} onClick={() => toggleSelect(name)}>
                    <span>{EMOJI_MAP[name]}</span>
                    <span>{name}</span>
                  </div>
                ))}
              </div>
              {selected.length > 0 && (
                <button style={s.goBtn} onClick={goToFirst}>View {selected[0]} Macros →</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}